import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { COLORS, SIZES } from '~/common/globalStyles'
import { ProjectType } from '~/common/types/graphql'
import Link, { Props as LinkProps } from '~/views/components/routing/Link'
import Tooltip from '~/views/components/ui/Tooltip'

interface StyledProgressBarProps {
  size?: keyof typeof SIZES
  height?: string
}

const StyledProgressBar = styled.div<StyledProgressBarProps>`
  width: 100%;
  height: ${({ size, height }) => (size ? SIZES[size] : height)};
  display: flex;
  position: relative;
  border-radius: 4px;
  overflow: hidden;
`

interface StyledBarProps {
  percentage?: number | null
  progressColor?: string
  useLink?: boolean
}

interface WrapperProps {
  className?: string
  children?: React.ReactNode
}

const Wrapper: React.FC<WrapperProps> = ({ children, className }) => {
  return <span className={className}>{children}</span>
}

const StyledBar = styled(
  ({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    percentage,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    progressColor,
    useLink,
    ...rest
  }: StyledBarProps & LinkProps) => {
    return <>{useLink ? <Link {...rest} /> : <Wrapper {...rest} />}</>
  },
)<StyledBarProps>`
  width: ${({ percentage }) => `${percentage ? percentage * 100 : 0}%`};
  height: 100%;
  background-color: ${({ progressColor }) =>
    COLORS[progressColor as keyof typeof COLORS] || progressColor};
  transition: width 200ms ease-in-out;
`

const TooltipBlock = styled.div`
  width: 100%;
  height: 100%;
`

interface MultipleProgressBarProps {
  projectId: ProjectType['id']
  firstActiveTemplateId: ProjectType['firstActiveTemplate']
  onClick: LinkProps['onClick']
  size?: keyof typeof SIZES
  useLink?: boolean
  values: {
    percentage?: number | null
    color?: string
    hoverText?: string | null
    status: string
  }[]
}

const MultipleProgressBar = ({
  projectId,
  firstActiveTemplateId,
  values,
  onClick,
  useLink = true,
  ...props
}: MultipleProgressBarProps) => {
  if (!projectId) return null

  return (
    <StyledProgressBar {...props} data-testid="styled-progress-bar">
      {values.map(({ hoverText, status, ...barProps }, index) => (
        <StyledBar
          useLink={useLink}
          key={index}
          onClick={onClick}
          href={{
            pathname: `/app/projects/${projectId}/plan/`,
            query: {
              'task-filters': JSON.stringify({ statuses: [status] }),
              'template-id': firstActiveTemplateId,
              filters: JSON.stringify([
                {
                  chipName: 'Status',
                  displayValue: `${hoverText}`,
                  name: 'statuses',
                  values: [status],
                },
              ]),
            },
          }}
          progressColor={barProps.color}
          percentage={barProps.percentage}
        >
          <Tooltip title={hoverText} placement="top">
            <TooltipBlock />
          </Tooltip>
        </StyledBar>
      ))}
    </StyledProgressBar>
  )
}

MultipleProgressBar.propTypes = {
  values: PropTypes.arrayOf(
    PropTypes.shape({
      percentage: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
      hoverText: PropTypes.string.isRequired,
    }),
  ).isRequired,
}

export default MultipleProgressBar
